import * as _ from "lodash";
import {
  Box2,
  Box3,
  Color,
  Line3,
  Matrix3,
  Matrix4,
  Object3D,
  Plane,
  Quaternion,
  Ray,
  Sphere,
  Vector2,
  Vector3,
} from "three";
import { OBB } from "three/examples/jsm/math/OBB";

declare module "three/examples/jsm/math/OBB" {
  interface OBB {
    toString(): string;
  }
}

declare module "three" {
  interface Object3D {
    toString(): string;
  }
  interface Vector3 {
    toString(): string;
  }
  interface Box3 {
    toString(): string;
  }
  interface Vector2 {
    toString(): string;
  }
  interface Box2 {
    toString(): string;
  }
  interface Sphere {
    toString(): string;
  }
  interface Ray {
    toString(): string;
  }
  interface Plane {
    toString(): string;
  }
  interface Quaternion {
    toString(): string;
  }
  interface Matrix3 {
    toString(): string;
  }
  interface Matrix4 {
    toString(): string;
  }
  interface Color {
    toString(): string;
  }
  interface Line3 {
    toString(): string;
  }
}

Object3D.prototype.toString = function (): string {
  const debugString = function (object: Object3D): string {
    if (object.name != null && object.name != "") {
      return object.name;
    }
    const funcNameRegex = /function (.{1,})\(/;
    const results = funcNameRegex.exec(object.constructor.toString());
    if (results && results.length > 1 && !(results[1] == "onRotationChange")) {
      return results[1];
    }
    const constructor_name = _.get(object, "constructor.name");
    if (constructor_name) {
      return constructor_name;
    }
    if (object.type) {
      return object.type;
    }
    return typeof object;
  };
  //const name = o => o.parent ? name(o.parent) + '/' + debugString(o) + '_' + o.id : debugString(o) + '_' + o.id;
  return debugString(this) + "_" + this.id;
};

Vector3.prototype.toString = function (): string {
  try {
    return "(" + this.x.toFixed(3) + "," + this.y.toFixed(3) + "," + this.z.toFixed(3) + ")";
  } catch (e) {
    return "(" + this.x + "," + this.y + "," + this.z + ")";
  }
};

Box3.prototype.toString = function (): string {
  return "(" + this.min + "," + this.max + ")";
};

Vector2.prototype.toString = function (): string {
  try {
    return "(" + this.x.toFixed(3) + "," + this.y.toFixed(3) + ")";
  } catch (e) {
    return "(" + this.x + "," + this.y + ")";
  }
};

Box2.prototype.toString = function (): string {
  return "(" + this.min + "," + this.max + ")";
};

Sphere.prototype.toString = function (): string {
  return "(c:" + this.center + ",r:" + this.radius + ")";
};

Ray.prototype.toString = function (): string {
  return "(o:" + this.origin + ",d:" + this.direction + ")";
};

Plane.prototype.toString = function (): string {
  return "(o:" + this.constant + ",normal:" + this.normal + ")";
};

Quaternion.prototype.toString = function (): string {
  return "(" + this.x + "," + this.y + "," + this.z + "," + this.w + ")";
};

Matrix3.prototype.toString = function (): string {
  try {
    const el = this.elements;
    const p = (i: number) => (el[i] >= 0 ? " " : "") + el[i].toFixed(2) + " ";
    return (
      "\n + " +
      p(0) +
      p(3) +
      p(6) +
      " +\n" +
      "| " +
      p(1) +
      p(4) +
      p(7) +
      " |\n" +
      "+ " +
      p(2) +
      p(5) +
      p(8) +
      " +\n"
    );
  } catch (e) {
    return JSON.stringify(this);
  }
};

Matrix4.prototype.toString = function (): string {
  try {
    const el = this.elements;
    const p = (i: number) => (el[i] >= 0 ? " " : "") + el[i].toFixed(2) + " ";
    return (
      "\n+ " +
      p(0) +
      p(4) +
      p(8) +
      p(12) +
      " +\n" +
      "| " +
      p(1) +
      p(5) +
      p(9) +
      p(13) +
      " |\n" +
      "| " +
      p(2) +
      p(6) +
      p(10) +
      p(14) +
      " |\n" +
      "+ " +
      p(3) +
      p(7) +
      p(11) +
      p(15) +
      " +\n"
    );
  } catch (e) {
    return JSON.stringify(this);
  }
};

Color.prototype.toString = function (): string {
  return "#" + this.getHexString();
};

Line3.prototype.toString = function (): string {
  return "(" + this.start + "," + this.end + ")";
};

OBB.prototype.toString = function (): string {
  return (
    "(center:" + this.center + ",halfSize:" + this.halfSize + ",rotation:" + this.rotation + ")"
  );
};
